import { jsPDF } from 'jspdf';
import edsiderLogo from '../../assets/images/edsiderlogo.png';

const DEFAULT_MARGIN = 14;
const DEFAULT_PAGE_WIDTH = 210;
const DEFAULT_PAGE_HEIGHT = 297;

const COLOR = {
    purple: [109, 40, 217],
    purpleSoft: [245, 243, 255],
    name: [39, 47, 60],
    navy: [17, 24, 39],
    muted: [107, 114, 128],
    text: [31, 41, 55],
    panel: [249, 250, 251],
    tableHead: [243, 244, 246],
    rowGrey: [255, 255, 255],
    rowAlt: [250, 250, 252],
    red: [185, 28, 28],
    line: [229, 231, 235],
    border: [209, 213, 219],
    success: [22, 101, 52],
};

const safeAmount = (value) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const formatAmount = (value, symbol = 'Rs.') => {
    const amount = safeAmount(value).toFixed(2);
    return `Rs. ${amount}`;
};

const defaultFormatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const pickFirst = (...values) => values.find((value) => value !== undefined && value !== null && String(value).trim() !== '') || '-';

const splitLines = (doc, value, width) => {
    const text = String(value ?? '-');
    return doc.splitTextToSize(text, width);
};

const normalizeCurrencySymbol = (symbol = '') => {
    const raw = String(symbol ?? '').trim();
    if (!raw) return 'Rs.';
    const normalized = raw.replace(/[\s\."']/g, '').toLowerCase();

    if (
        normalized === '' ||
        normalized === '₹' ||
        normalized === 'rs' ||
        normalized === 'rs.' ||
        normalized === 'inr' ||
        normalized === 'lkr'
    ) {
        return 'Rs.';
    }

    if (raw.includes('₹')) {
        return 'Rs.';
    }

    return raw;
};

const currencySymbolFrom = (invoice, order) => normalizeCurrencySymbol(invoice?.currency_symbol || order?.currency_symbol || 'Rs.');

const getDiscountLabel = (invoice) => {
    const reason = String(invoice?.discount_reason || invoice?.discount_label || invoice?.discount_type || '').toLowerCase();
    if (reason.includes('loyalty')) return 'Discount (Client Loyalty)';
    if (reason) return `Discount (${invoice.discount_reason || invoice.discount_label || invoice.discount_type})`;
    return 'Discount';
};

const getTaxLabel = (invoice) => {
    const rate = invoice?.tax_rate ?? invoice?.tax_percentage ?? invoice?.tax_percent;
    if (rate !== undefined && rate !== null && rate !== '') {
        const numericRate = Number(rate);
        if (Number.isFinite(numericRate)) {
            const displayRate = Number.isInteger(numericRate)
                ? String(numericRate)
                : numericRate.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
            return `Tax (${displayRate}%)`;
        }
    }
    return 'Tax';
};

const getItemDescription = (item) => {
    const explicitDescription = pickFirst(item?.item_description, item?.description, item?.description_text, item?.item_description_text);
    if (explicitDescription !== '-') return explicitDescription;

    if (String(item?.item_type).toLowerCase() === 'credit_package') {
        return 'Credit Package';
    }
    if (String(item?.item_type).toLowerCase() === 'assessment') {
        return item?.item_name ? `${item.item_name} Assessment` : 'Assessment';
    }
    return item?.item_name || '-';
};

const getCompanyLines = () => ([
    'EdSider (Pvt) Ltd.',
    'Phone: +94 11 255 0173',
    'Email: info@edsider.com',
    'Address: Level 4, 135 Bauddhaloka Mawatha,',
    '                    Colombo 00400, Sri Lanka',
]);

const getBillToLines = (invoice, order) => {
    let name = pickFirst(
        invoice?.created_by_user_name,
        invoice?.customer_name,
        invoice?.bill_to_name,
        invoice?.billing_name,
        order?.customer_name,
        order?.user_name,
        order?.customer_email,
        invoice?.user_name,
        invoice?.user_email
    );

    if (name === '-') {
        name = 'Biller Name (Hardcoded)';
    }

    const lines = [name];
    const userPhone = pickFirst(invoice?.created_by_user_phone);
    const userEmail = pickFirst(invoice?.created_by_user_email);
    if (userPhone !== '-') {
        lines.push(`Phone: ${userPhone}`);
    }
    if (userEmail !== '-') {
        lines.push(`Email: ${userEmail}`);
    }

    const fullBillingAddress = pickFirst(invoice?.full_billing_address);
    if (fullBillingAddress !== '-') {
        const addressParts = fullBillingAddress.split(',').map(part => part.trim());
        const midPoint = Math.ceil(addressParts.length / 2);
        const line1 = addressParts.slice(0, midPoint).join(', ');
        const line2 = addressParts.slice(midPoint).join(', ');
        lines.push(`Billing Address: ${line1}`);
        if (line2) lines.push(line2);
    }

    const attn = pickFirst(invoice?.attention_to, invoice?.attn_name, order?.attention_to);
    if (attn !== '-' && attn !== name) lines.push(`Attn: ${attn}`);

    const addressLine1 = pickFirst(invoice?.billing_address, invoice?.address_line1, order?.billing_address, order?.address_line1);
    const addressLine2 = pickFirst(invoice?.billing_address_2, invoice?.address_line2, order?.address_line2);
    const cityLine = [pickFirst(invoice?.billing_city, order?.billing_city), pickFirst(invoice?.billing_state, order?.billing_state), pickFirst(invoice?.billing_zip, order?.billing_zip)]
        .filter((part) => part !== '-')
        .join(', ');

    if (addressLine1 !== '-') lines.push(addressLine1);
    if (addressLine2 !== '-') lines.push(addressLine2);
    if (cityLine) lines.push(cityLine);

    return lines.slice(0, 6);
};

const drawTopStripe = (doc, pageWidth) => {
    doc.setFillColor(...COLOR.purple);
    doc.rect(0, 0, pageWidth, 3, 'F');
};

const drawHeader = (doc, logoImage, invoice, order, formatDate, pageWidth, margin) => {
    drawTopStripe(doc, pageWidth);

    if (logoImage) {
        doc.addImage(logoImage, 'PNG', margin, 12, 45, 14);
    }

    doc.setTextColor(...COLOR.navy);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(24);
    doc.text('Invoice', margin, 42);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...COLOR.muted);
    doc.text('Generated invoice summary', margin, 48);

    const cardWidth = 74;
    const cardX = pageWidth - margin - cardWidth;
    const cardY = 12;
    const labelX = cardX + 6;
    const valueX = cardX + cardWidth - 6;
    const metaTop = cardY + 9;
    const paymentMode = pickFirst(
        invoice?.payment_method,
        invoice?.payment_mode,
        order?.payment_method,
        order?.payment_mode
    );

    doc.setFillColor(...COLOR.panel);
    doc.setDrawColor(...COLOR.line);
    doc.roundedRect(cardX, cardY, cardWidth, 39, 2, 2, 'FD');

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.4);
    doc.setTextColor(...COLOR.muted);
    doc.text('INVOICE #', labelX, metaTop, { align: 'left' });
    doc.text('DATE ISSUED', labelX, metaTop + 7, { align: 'left' });
    doc.text('PAYMENT MODE', labelX, metaTop + 14, { align: 'left' });
    doc.text('STATUS', labelX, metaTop + 21, { align: 'left' });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...COLOR.navy);
    doc.text(String(invoice?.invoice_id || '-'), valueX, metaTop, { align: 'right' });
    doc.text(formatDate(invoice?.created_at), valueX, metaTop + 7, { align: 'right', maxWidth: 38 });
    doc.text(paymentMode, valueX, metaTop + 14, { align: 'right' });
    doc.text(String(order?.status || invoice?.status || '-'), valueX, metaTop + 21, { align: 'right' });

    const headerBottom = 56;
    doc.setDrawColor(...COLOR.line);
    doc.line(margin, headerBottom, pageWidth - margin, headerBottom);
};

const drawInfoPanel = (doc, x, y, width, title, lines) => {
    const lineHeight = 4.6;
    const titleHeight = 5;
    const panelHeight = 13 + titleHeight + lines.length * lineHeight;

    doc.setFillColor(...COLOR.panel);
    doc.setDrawColor(...COLOR.line);
    doc.roundedRect(x, y, width, panelHeight, 2, 2, 'FD');

    doc.setTextColor(...COLOR.muted);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.2);
    doc.text(title.toUpperCase(), x + 5, y + 7);

    doc.setDrawColor(...COLOR.purple);
    doc.setLineWidth(0.5);
    doc.line(x + 5, y + 9.2, x + 22, y + 9.2);

    let currentY = y + 16;
    doc.setFontSize(8.5);
    lines.forEach((line, index) => {
        const text = String(line);
        const match = text.match(/^(.+?:)\s*(.*)$/);
        const isFirstLine = index === 0;

        if (match) {
            const [, label, value] = match;
            const labelWithSpace = `${label} `;

            doc.setTextColor(...COLOR.text);
            doc.setFont('helvetica', 'bold');
            doc.text(label, x + 5, currentY);

            const labelWidth = doc.getTextWidth(labelWithSpace);
            doc.setTextColor(...COLOR.text);
            doc.setFont('helvetica', 'normal');
            if (value) {
                doc.text(value, x + 5 + labelWidth, currentY);
            }
        } else {
            if (isFirstLine) {
                doc.setTextColor(...COLOR.name);
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(9.2);
            } else {
                doc.setTextColor(...COLOR.text);
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(8.5);
            }
            doc.text(text, x + 5, currentY);
        }

        currentY += lineHeight;
    });

    return panelHeight;
};

const drawTableHeader = (doc, x, y, widths) => {
    const height = 14;
    const totalWidth = widths.reduce((sum, value) => sum + value, 0);

    doc.setFillColor(...COLOR.tableHead);
    doc.setDrawColor(...COLOR.line);
    doc.roundedRect(x, y, totalWidth, height, 1.6, 1.6, 'FD');

    doc.setTextColor(...COLOR.muted);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5.9);

    let colX = x + 4;
    const twoLine = (label, centerX) => {
        if (Array.isArray(label)) {
            doc.text(label[0], centerX, y + 5, { align: 'center' });
            doc.text(label[1], centerX, y + 9.2, { align: 'center' });
            return;
        }
        doc.text(label, centerX, y + 7.4, { align: 'center' });
    };

    doc.text('ITEM', colX, y + 7.4);
    colX += widths[0];
    twoLine('TYPE', colX + widths[1] / 2);
    colX += widths[1];
    twoLine('QTY', colX + widths[2] / 2);
    colX += widths[2];
    twoLine('CREDITS', colX + widths[3] / 2);
    colX += widths[3];
    twoLine(['UNIT', 'PRICE'], colX + widths[4] / 2);
    colX += widths[4];
    twoLine(['UNIT', 'DISCOUNT'], colX + widths[5] / 2);
    colX += widths[5];
    twoLine(['UNIT', 'TOTAL'], colX + widths[6] / 2);
    colX += widths[6];
    twoLine(['TOTAL', 'DISCOUNT'], colX + widths[7] / 2);
    colX += widths[7];
    twoLine('TOTAL', colX + widths[8] / 2);

    return height;
};

const measureItemRow = (doc, item, width) => {
    const titleLines = splitLines(doc, item.item_name || '-', width);
    const description = getItemDescription(item);
    const descriptionLines = splitLines(doc, description, width);
    return Math.max(16, 5.8 + (titleLines.length * 4.2) + (descriptionLines.length * 3.8) + 2.8);
};

const drawItemRow = (doc, x, y, widths, item, currencySymbol, rowIndex) => {
    const totalWidth = widths.reduce((sum, value) => sum + value, 0);
    const rowHeight = measureItemRow(doc, item, widths[0] - 8);

    const rowFillColor = rowIndex % 2 === 0 ? COLOR.rowGrey : COLOR.rowAlt;
    doc.setFillColor(...rowFillColor);
    doc.rect(x, y, totalWidth, rowHeight, 'F');

    const titleLines = splitLines(doc, item.item_name || '-', widths[0] - 8);
    const description = getItemDescription(item);
    const userNameLine = item?.user_name ? `(${item.user_name})` : '';
    const descriptionLines = userNameLine 
        ? [userNameLine, ...splitLines(doc, description, widths[0] - 8)]
        : splitLines(doc, description, widths[0] - 8);
    const titleHeight = titleLines.length * 4.2;
    const descriptionHeight = descriptionLines.length * 3.8;
    const spacingBetween = 1.0;
    const contentHeight = titleHeight + descriptionHeight + spacingBetween;
    const textTop = y + Math.max(7, (rowHeight - contentHeight) / 2 + 3);

    doc.setTextColor(...COLOR.text);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.text(titleLines, x + 4, textTop);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.2);
    doc.setTextColor(104, 108, 118);
    doc.text(descriptionLines, x + 4, textTop + titleHeight + spacingBetween);

    const middleY = y + rowHeight / 2 + 1;
    let colX = x + widths[0];
    
    // Type
    const itemType = String(item?.item_type).toLowerCase() === 'credit_package' ? 'Credit Package' : 'Assessment';
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLOR.text);
    doc.setFontSize(7.5);
    doc.text(splitLines(doc, itemType, widths[1] - 2), colX + widths[1] / 2, middleY - 2, { align: 'center' });
    colX += widths[1];

    // Qty
    doc.setFontSize(8.7);
    doc.text(String(item.quantity ?? 1), colX + widths[2] / 2, middleY, { align: 'center' });
    colX += widths[2];

    // Credits
    const creditCount = String(item?.credits ?? (String(item?.item_type).toLowerCase() === 'credit_package' ? item.quantity : '-'));
    doc.text(creditCount, colX + widths[3] / 2, middleY, { align: 'center' });
    colX += widths[3];

    // Unit Price
    doc.setFontSize(7.6);
    doc.text(formatAmount(item.unit_price, currencySymbol), colX + widths[4] - 2, middleY, { align: 'right' });
    colX += widths[4];

    // Unit Discount
    doc.setFontSize(7.6);
    doc.text(formatAmount(item.unit_discount || 0, currencySymbol), colX + widths[5] - 2, middleY, { align: 'right' });
    colX += widths[5];

    // Unit Total Price (unit_price * quantity)
    const unitTotalPrice = (parseFloat(item.unit_price || 0) * parseFloat(item.quantity || 0)).toFixed(2);
    doc.setFontSize(7.6);
    doc.text(formatAmount(unitTotalPrice, currencySymbol), colX + widths[6] - 2, middleY, { align: 'right' });
    colX += widths[6];

    // Total Discount
    doc.setFontSize(7.6);
    doc.text(formatAmount(item.total_discount || 0, currencySymbol), colX + widths[7] - 2, middleY, { align: 'right' });
    colX += widths[7];

    // Total Price
    doc.setFontSize(7.6);
    doc.setFont('helvetica', 'bold');
    doc.text(formatAmount(item.total_price, currencySymbol), colX + widths[8] - 2, middleY, { align: 'right' });

    doc.setDrawColor(...COLOR.line);
    doc.setLineWidth(0.25);
    doc.line(x + 2, y + rowHeight, x + totalWidth - 2, y + rowHeight);

    return rowHeight;
};

const drawTotals = (doc, x, y, width, invoice, currencySymbol, subtotal) => {
    const discount = safeAmount(invoice?.discount_amount);
    const tax = safeAmount(invoice?.tax_amount);
    const totalDue = safeAmount(invoice?.total_price) || Math.max(subtotal - discount + tax, 0);
    const rowGap = 6.6;
    const labelX = x + 6;
    const valueX = x + width - 6;

    doc.setFillColor(...COLOR.panel);
    doc.setDrawColor(...COLOR.line);
    doc.roundedRect(x, y - 5, width, 34, 2, 2, 'FD');

    let currentY = y + 1;
    const drawLine = (label, value, amountColor, bold = false) => {
        doc.setFont('helvetica', bold ? 'bold' : 'normal');
        doc.setFontSize(bold ? 9.7 : 8.2);
        doc.setTextColor(...COLOR.text);
        doc.text(label, labelX, currentY);
        doc.setTextColor(...amountColor);
        doc.text(value, valueX, currentY, { align: 'right' });
        currentY += rowGap;
    };

    drawLine('Subtotal', formatAmount(subtotal, currencySymbol), COLOR.text);

    if (discount > 0) {
        drawLine(getDiscountLabel(invoice), `-${formatAmount(discount, currencySymbol)}`, COLOR.red);
    }

    if (tax > 0) {
        drawLine(getTaxLabel(invoice), formatAmount(tax, currencySymbol), COLOR.text);
    }

    doc.setDrawColor(...COLOR.border);
    doc.setLineWidth(0.4);
    doc.line(x + 5, currentY - 1, x + width - 5, currentY - 1);

    const totalLabelX = x + 6;
    const totalValueX = x + width - 6;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11.8);
    doc.setTextColor(...COLOR.navy);
    doc.text('TOTAL AMOUNT', totalLabelX, currentY + 4.5);
    doc.text(formatAmount(totalDue, currencySymbol), totalValueX, currentY + 4.5, { align: 'right' });

    return currentY + 10;
};

const drawSignatureBlock = (doc, pageWidth, pageHeight, margin) => {
    const labelX = pageWidth - 45;
    const labelY = pageHeight - 30;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...COLOR.text);
    doc.text('AUTHORIZED SIGNATURE', labelX, labelY);

    doc.setDrawColor(...COLOR.line);
    doc.setLineWidth(0.35);
    doc.line(labelX - 1, labelY + 13, pageWidth - margin, labelY + 13);

    doc.setFillColor(206, 208, 214);
    doc.rect(pageWidth - 24, labelY + 4, 8.5, 8.5, 'F');
};

const drawThankYou = (doc, margin, pageHeight) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.2);
    doc.setTextColor(...COLOR.navy);
    doc.text('Thank you for purchasing with us!', margin, pageHeight - 16);
};

const drawFooter = (doc, pageWidth, pageHeight) => {
    const footerHeight = 3;
    doc.setFillColor(...COLOR.purple);
    doc.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, 'F');
};

export const downloadInvoicePDF = (order, invoice, formatDate = defaultFormatDate) => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth?.() || DEFAULT_PAGE_WIDTH;
    const pageHeight = doc.internal.pageSize.getHeight?.() || DEFAULT_PAGE_HEIGHT;
    const margin = DEFAULT_MARGIN;
    const currencySymbol = currencySymbolFrom(invoice, order);

    const items = invoice?.invoice_items?.length ? invoice.invoice_items : (order?.order_items || []);
    const subtotal = items.reduce((sum, item) => sum + safeAmount(item.total_price), 0);

    const renderInvoice = (logoImage) => {
        doc.setFillColor(255, 255, 255);
        doc.rect(0, 0, pageWidth, pageHeight, 'F');
        doc.setCharSpace(-0.1);

        drawHeader(doc, logoImage, invoice, order, formatDate, pageWidth, margin);

        const infoY = 58;
        const panelGap = 4;
        const panelWidth = (pageWidth - (margin * 2) - panelGap) / 2;
        const companyLines = getCompanyLines();
        const billToLines = getBillToLines(invoice, order);
        const leftHeight = drawInfoPanel(doc, margin, infoY, panelWidth, 'Company Information', companyLines);
        const rightHeight = drawInfoPanel(doc, margin + panelWidth + panelGap, infoY, panelWidth, 'Bill To', billToLines);
        // const rightHeight = 0; // Bill To section commented
        const tableWidths = [48, 18, 9, 12, 19, 19, 19, 21, 17];
        const tableStartY = infoY + Math.max(leftHeight, rightHeight) + 10;

        const tableHeaderHeight = drawTableHeader(doc, margin, tableStartY, tableWidths);

        let currentY = tableStartY + tableHeaderHeight;
        items.forEach((item, index) => {
            const estimatedHeight = measureItemRow(doc, item, tableWidths[0] - 8);
            if (currentY + estimatedHeight > pageHeight - 48) {
                doc.addPage();
                drawTopStripe(doc, pageWidth);
                currentY = 20;
                currentY += drawTableHeader(doc, margin, currentY, tableWidths);
            }

            const rowHeight = drawItemRow(doc, margin, currentY, tableWidths, item, currencySymbol, index);
            currentY += rowHeight;
        });

        const totalsStartY = Math.max(currentY + 14, 153);
        const totalsWidth = 76;
        const totalsX = pageWidth - margin - totalsWidth;
        drawTotals(doc, totalsX, totalsStartY, totalsWidth, invoice, currencySymbol, subtotal);

        drawThankYou(doc, margin, pageHeight);
        drawSignatureBlock(doc, pageWidth, pageHeight, margin);
        drawFooter(doc, pageWidth, pageHeight);

        doc.save(`Invoice_${invoice?.invoice_id || 'download'}.pdf`);
    };

    const logoImage = new Image();
    logoImage.crossOrigin = 'anonymous';
    logoImage.onload = () => renderInvoice(logoImage);
    logoImage.onerror = () => renderInvoice(null);
    logoImage.src = edsiderLogo;
};

export default downloadInvoicePDF;
